import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { useCart } from "../context/CartContext";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clearCart } = useCart();
  const [status, setStatus] = useState("checking");

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          clearCart();
          return;
        }
      } catch {
        /* keep polling */
      }
      if (attempts >= 6) {
        setStatus("pending");
        return;
      }
      setTimeout(poll, 2000);
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div data-testid="payment-success-page" className="max-w-xl mx-auto px-6 py-32 text-center">
      {status === "checking" && (
        <>
          <Loader2 size={40} className="mx-auto mb-6 animate-spin text-[#666]" />
          <h1 className="font-serif text-4xl mb-3">Confirming your payment…</h1>
          <p className="text-[#666]">Please wait a moment.</p>
        </>
      )}
      {status === "paid" && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="w-16 h-16 bg-[#1a1a1a] text-white rounded-full flex items-center justify-center mx-auto mb-8">
            <Check size={28} />
          </div>
          <h1 className="font-serif text-5xl tracking-tight mb-4">Thank you</h1>
          <p className="text-[#666] mb-10">
            Your order is confirmed. We'll send you tracking details as soon as it ships.
          </p>
          <div className="flex gap-4 justify-center">
            <Link to="/account" data-testid="view-orders-btn" className="bg-[#1a1a1a] text-white px-8 py-4 text-sm uppercase tracking-luxe hover:bg-[#333]">
              View Orders
            </Link>
            <Link to="/shop" className="border border-[#1a1a1a] px-8 py-4 text-sm uppercase tracking-luxe hover:bg-[#f5f5f0]">
              Keep Shopping
            </Link>
          </div>
        </motion.div>
      )}
      {status === "pending" && (
        <>
          <h1 className="font-serif text-4xl mb-3">Payment processing</h1>
          <p className="text-[#666] mb-8">
            Your payment is still processing. Check your account for updates shortly.
          </p>
          <Link to="/account" className="bg-[#1a1a1a] text-white px-8 py-4 text-sm uppercase tracking-luxe">View Orders</Link>
        </>
      )}
      {status === "error" && (
        <>
          <h1 className="font-serif text-4xl mb-3">Something went wrong</h1>
          <Link to="/cart" className="text-black underline">Return to cart</Link>
        </>
      )}
    </div>
  );
}
